import { getCPDataWithoutParams } from 'src/clinic/services'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import { IDB_STORES } from 'src/consumer/constants'
import {
  getAllRecordsFromStore,
  putRecordsInStore,
  searchStoreByTitleFast,
} from 'src/emr/database/indexedDbDao'

/**
 * Local-first data for the patient form.
 *
 * Cities: the platform city database for a country is pulled once (pages of 500) into the
 * `meta_cities` IndexedDB store, so the City field searches locally and instantly (and works
 * offline). Until the store is warm, searches fall back to the API.
 *
 * Tag suggestions: the brand's tag library is cached per brand in `patient_tag_suggestions`
 * so chips appear immediately; the API refresh runs in the background and upserts.
 */

const CITY_SEARCH_API = '/api/secure/location/city/search'
const CITY_REMOTE_PAGE_SIZE = 12
const CITY_STORE = IDB_STORES.META_CITIES.name
const TAG_STORE = IDB_STORES.PATIENT_TAG_SUGGESTIONS.name

// NOTE (2026-09-09 prod incident): the first version of this cache pulled a country's whole
// city table (pages of 500, with a find + count per page) for every user opening the form.
// That saturated production. Cities are now fetched only per typed term (small pages) and the
// results are cached locally, so repeat searches never hit the server again.

const remoteSearchCache = new Map()

function normalizeIso(iso) {
  return typeof iso === 'string' && /^[A-Za-z]{2}$/.test(iso.trim()) ? iso.trim().toUpperCase() : ''
}

export function getCityLabel(city) {
  if (!city) return ''
  return city.title || city.name || city.label || ''
}

export function getCityStateLabel(city) {
  return city?.administrativeEntityName || city?.state || ''
}

function toCityRecord(city, iso) {
  const title = getCityLabel(city)
  if (!title) return null
  const countryCode = normalizeIso(city?.countryCode) || iso
  return {
    id: city?.id || `${countryCode}:${title}:${getCityStateLabel(city)}`.toLowerCase(),
    title,
    countryCode,
    country: city?.country,
    administrativeEntityName: city?.administrativeEntityName,
    relatedNames: Array.isArray(city?.relatedNames) ? city.relatedNames.slice(0, 5) : undefined,
    population: city?.population,
    lowerCaseIndexField: title.toLowerCase(),
  }
}

/** Kept for callers; the bulk warm-up is intentionally gone. Returns false (nothing prefetched). */
export async function ensureCitiesCached() {
  return false
}

export function areCitiesCached() {
  return false
}

async function fetchCitySearch({ search, iso, pageSize = CITY_REMOTE_PAGE_SIZE }) {
  const key = `${iso || ''}|${search}`
  if (remoteSearchCache.has(key)) return remoteSearchCache.get(key)
  const params = new URLSearchParams({ search, page: '1', pageSize: String(pageSize) })
  if (iso) params.set('countryCode', iso.toLowerCase())
  const task = (async () => {
    try {
      const response = await getCPDataWithoutParams(`${CITY_SEARCH_API}?${params.toString()}`)
      if (response?.status !== 200) return []
      const content = response?.data?.content
      return Array.isArray(content) ? content : []
    } catch (error) {
      return []
    }
  })()
  remoteSearchCache.set(key, task)
  return task
}

/** The city database holds a few towns twice (with and without a state); show each once. */
function dedupeCities(rows) {
  const byTitle = new Map()
  rows.forEach((row) => {
    const key = `${row.lowerCaseIndexField || getCityLabel(row).toLowerCase()}|${row.countryCode || ''}`
    const existing = byTitle.get(key)
    if (!existing || (!getCityStateLabel(existing) && getCityStateLabel(row))) {
      byTitle.set(key, row)
    }
  })
  return rows.filter((row) => {
    const key = `${row.lowerCaseIndexField || getCityLabel(row).toLowerCase()}|${row.countryCode || ''}`
    return byTitle.get(key) === row
  })
}

function rankCity(record, needle) {
  const title = record.lowerCaseIndexField || ''
  if (title === needle) return 0
  if (title.startsWith(needle)) return 1
  return 2
}

function sortCities(rows, needle) {
  return rows.sort(
    (a, b) => rankCity(a, needle) - rankCity(b, needle) || (b.population || 0) - (a.population || 0),
  )
}

/**
 * Search cities for a typed term: the local cache answers first; when it has fewer than
 * `limit` matches the server is asked for that term only (12 rows) and the answer is cached
 * locally for next time. Never fetches without a term.
 */
export async function searchCities(term, { iso, limit = 8 } = {}) {
  const needle = String(term || '').trim().toLowerCase()
  const normalized = normalizeIso(iso)
  if (needle.length < 2) return []

  let local = []
  try {
    local = await searchStoreByTitleFast(CITY_STORE, needle, { limit: limit * 3 })
  } catch (error) {
    local = []
  }
  if (normalized) {
    local = local.filter((row) => !row.countryCode || row.countryCode === normalized)
  }
  local = dedupeCities(sortCities(local, needle))
  if (local.length >= limit) {
    return local.slice(0, limit)
  }

  const remoteRows = await fetchCitySearch({ search: needle, iso: normalized })
  const remote = remoteRows.map((row) => toCityRecord(row, normalized)).filter(Boolean)
  if (remote.length > 0) {
    putRecordsInStore(CITY_STORE, remote).catch(() => {})
  }
  const seen = new Set(local.map((row) => row.id))
  const merged = [...local, ...remote.filter((row) => !seen.has(row.id))]
  return dedupeCities(sortCities(merged, needle)).slice(0, limit)
}

// ───────────────────────── tag suggestions ─────────────────────────

function tagKey(tag) {
  return (tag?.tagCode || tag?.label || '').toString().trim().toLowerCase()
}

function toTagRecord(tag, brandId) {
  const key = tagKey(tag)
  if (!key || !tag?.label) return null
  return {
    ...tag,
    id: `${brandId || 'any'}:${key}`,
    clinicBrandId: brandId || 'any',
    lowerCaseIndexField: tag.label.toLowerCase(),
    cachedAt: Date.now(),
  }
}

export async function getCachedTagSuggestions(brandId) {
  try {
    const all = await getAllRecordsFromStore(TAG_STORE)
    const scope = brandId || 'any'
    return all
      .filter((row) => row.clinicBrandId === scope)
      .sort((a, b) => (b.lastUsedTime || b.cachedAt || 0) > (a.lastUsedTime || a.cachedAt || 0) ? 1 : -1)
  } catch (error) {
    return []
  }
}

export async function cacheTagSuggestions(tags, brandId) {
  const records = (tags || []).map((tag) => toTagRecord(tag, brandId)).filter(Boolean)
  if (records.length === 0) return
  try {
    await putRecordsInStore(TAG_STORE, records)
  } catch (error) {
    // cache only
  }
}

/** Fetch the brand's suggestions from the portal's tags API and refresh the cache. */
export async function refreshTagSuggestions(patientTagsApiSignature, brandId) {
  if (!patientTagsApiSignature) return []
  const params = new URLSearchParams({ page: '1', pageSize: '50' })
  if (brandId) params.set('clinicBrandId', brandId)
  const url = `${patientTagsApiSignature}/suggestions?${params.toString()}`
  try {
    const response = patientTagsApiSignature.includes('/support/')
      ? await getSPDataWithParams(url, {})
      : await getCPDataWithoutParams(url)
    const content = response?.data?.data?.content || response?.data?.content || []
    const tags = Array.isArray(content) ? content : []
    await cacheTagSuggestions(tags, brandId)
    return tags
  } catch (error) {
    return []
  }
}
